import * as React from 'react';

import { Row as AntRow, Col as AntCol } from 'antd';
import { Column, Row, Space, Text } from 'farmhub-ui-core';
import moment from 'moment';

import { useAppSelector } from 'app/hooks';
import { NameText } from 'components/Text';
import { env } from 'env';
import { selectAppState } from 'slice';
import {
  formatMoney,
  formatAppearPhoneNumber,
  getOrderItemTotalPrice,
  getFullAddress,
  buildEntityDomain,
} from 'utils/function';

import K58BillSkeleton from '../components/K58BillSkeleton';
import { CQRCode, Hr, RowInfo, RowTableInfo, Wrapper } from './styles';

interface K58BillContentProps {
  bill: any;
  isCart?: boolean;
  isLoading?: boolean;
}

const K58BillContent = ({
  bill,
  isCart = false,
  isLoading = false,
}: K58BillContentProps): JSX.Element => {
  const app = useAppSelector(selectAppState);
  const entityID = useAppSelector(state => state.account.item?.id);

  return (
    <Wrapper className="bill-content">
      {isLoading ? (
        <K58BillSkeleton />
      ) : (
        <>
          <NameText
            color="black"
            styles={{ 'text-align': 'center' }}
            weight="bold"
          >
            {bill?.supplier?.displayName}
          </NameText>
          <NameText
            color="black"
            lineHeight="18px"
            size="tiny"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {buildEntityDomain({
              entityType: bill?.supplier?.type,
              entitySlug: bill?.supplier?.slug,
              domain: bill?.supplier?.domain,
              domainStatus: bill?.supplier?.domainStatus,
              website: bill?.supplier?.website,
            })}
          </NameText>
          <NameText
            color="black"
            lineHeight="18px"
            size="tiny"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {bill?.supplier &&
              getFullAddress(
                bill?.supplier?.address,
                bill?.supplier?.ward?.district?.fullName,
                bill?.supplier?.ward?.province?.fullName,
                bill?.supplier?.ward?.fullName
              )}
          </NameText>
          <Hr type="dashed" />
          <NameText color="black" lineHeight="20px" weight="bold">
            HÓA ĐƠN BÁN HÀNG
          </NameText>
          <NameText
            color="black"
            lineHeight="20px"
            size="tiny"
            styles={{ 'text-align': 'center' }}
            weight="regular"
          >
            {moment().format('DD/MM/YYYY HH:mm')}
          </NameText>
          <Space height={4} />
          {!isCart && (
            <Column alignItems="center" width="120px">
              <CQRCode
                level="L"
                renderAs="svg"
                value={`${
                  app?.domains?.hubBilling?.domain ??
                  env.service.hubbillingWebApp
                }/${bill.code}`}
              />
              <Space height={4} />
              <NameText
                color="black"
                lineHeight="18px"
                lineNumber={2}
                size="tiny"
                styles={{ 'text-align': 'center' }}
                weight="bold"
              >
                Mã thông tin hóa đơn
              </NameText>
            </Column>
          )}

          <Space height={12} />
          {!isCart && (
            <AntRow style={{ width: '100%' }}>
              <AntCol flex="50px">
                <NameText
                  color="black"
                  lineHeight="18px"
                  size="tiny"
                  weight="regular"
                >
                  Mã ĐH:
                </NameText>
              </AntCol>
              <AntCol flex="calc(100% - 50px)">
                <NameText
                  color="black"
                  lineHeight="18px"
                  size="tiny"
                  weight="regular"
                >
                  #{bill?.code}
                </NameText>
              </AntCol>
            </AntRow>
          )}
          <AntRow style={{ width: '100%' }}>
            <AntCol flex="50px">
              <NameText
                color="black"
                lineHeight="18px"
                size="tiny"
                weight="regular"
              >
                K.Hàng:
              </NameText>
            </AntCol>
            <AntCol flex="calc(100% - 50px)">
              <NameText
                className="info"
                color="black"
                lineHeight="18px"
                size="tiny"
                weight="regular"
              >
                {bill?.customer?.displayName}
              </NameText>
            </AntCol>
          </AntRow>
          <AntRow style={{ width: '100%' }}>
            <AntCol flex="50px">
              <NameText
                color="black"
                lineHeight="18px"
                size="tiny"
                weight="regular"
              >
                Họ tên:
              </NameText>
            </AntCol>
            <AntCol flex="calc(100% - 50px)">
              <NameText
                className="info"
                color="black"
                lineHeight="18px"
                size="tiny"
              >
                {bill?.additionalInfo?.address?.title}
              </NameText>
            </AntCol>
          </AntRow>
          <AntRow style={{ width: '100%' }}>
            <AntCol flex="50px">
              <NameText
                color="black"
                lineHeight="18px"
                size="tiny"
                weight="regular"
              >
                SĐT:
              </NameText>
            </AntCol>
            <AntCol flex="calc(100% - 50px)">
              <NameText
                className="info"
                color="black"
                lineHeight="18px"
                size="tiny"
              >
                {formatAppearPhoneNumber(bill?.customer?.phoneNumber)}
              </NameText>
            </AntCol>
          </AntRow>
          <AntRow style={{ width: '100%' }}>
            <AntCol flex="50px">
              <NameText
                color="black"
                lineHeight="18px"
                size="tiny"
                weight="regular"
              >
                Địa chỉ:
              </NameText>
            </AntCol>
            <AntCol flex="calc(100% - 50px)">
              <NameText
                className="info"
                color="black"
                lineHeight="18px"
                size="tiny"
              >
                {getFullAddress(
                  bill?.additionalInfo?.address?.address,
                  bill?.additionalInfo?.address?.district?.fullName,
                  bill?.additionalInfo?.address?.province?.fullName,
                  bill?.additionalInfo?.address?.ward?.fullName
                )}
              </NameText>
            </AntCol>
          </AntRow>
          <Space height={8} />
          <Hr type="solid" />
          <RowTableInfo>
            <NameText
              color="black"
              lineHeight="18px"
              size="tiny"
              style={{ width: '32%' }}
              weight="bold"
            >
              Đơn giá
            </NameText>
            <NameText
              className="order"
              color="black"
              lineHeight="18px"
              size="tiny"
              style={{ width: '35%' }}
              weight="bold"
            >
              SL.Đặt/Chốt
            </NameText>
            <NameText
              color="black"
              lineHeight="18px"
              size="tiny"
              style={{ width: '32%', textAlign: 'end' }}
              weight="bold"
            >
              T.Tiền
            </NameText>
          </RowTableInfo>
          <Hr type="solid" />
          {(bill?.orderItems ?? []).map(orderItem => (
            <>
              <Row justifyContent="flex-start">
                <NameText
                  color="black"
                  lineHeight="18px"
                  size="tiny"
                  styles={{ 'text-transform': 'uppercase' }}
                  weight="regular"
                >
                  {orderItem.product && orderItem.product?.name}
                  {/* case bill in cart modal */}
                  {orderItem.supplierProduct && bill?.customer?.id === entityID
                    ? orderItem.customerProduct?.name
                    : orderItem.supplierProduct?.name}
                </NameText>
              </Row>
              <RowTableInfo>
                <NameText
                  color="black"
                  lineHeight="18px"
                  size="small"
                  style={{ width: '32%' }}
                  weight="regular"
                >
                  {orderItem.product && formatMoney(orderItem?.price)}
                  {/* case bill in cart modal */}
                  {orderItem.supplierProduct &&
                    (bill?.customer?.id === entityID
                      ? formatMoney(orderItem.customerProduct?.price)
                      : formatMoney(orderItem.supplierProduct?.price))}
                  đ
                </NameText>
                <NameText
                  className="order"
                  color="black"
                  lineHeight="18px"
                  size="small"
                  style={{ width: '35%', textAlign: 'center' }}
                  weight="regular"
                >
                  {orderItem.customerQuantity}/
                  {orderItem.supplierQuantity || '..........'}
                </NameText>
                <NameText
                  color="black"
                  lineHeight="18px"
                  size="small"
                  style={{ width: '32%', textAlign: 'end' }}
                  weight="regular"
                >
                  {orderItem.product &&
                    getOrderItemTotalPrice(
                      orderItem,
                      bill?.status,
                      bill?.trackingStatus
                    )}
                  {/* case bill in cart modal */}
                  {orderItem.supplierProduct && formatMoney(orderItem.price)}đ
                </NameText>
              </RowTableInfo>
              <Hr type="dashed" />
            </>
          ))}
          <RowInfo justify="flex-end" left={-1}>
            <NameText
              color="black"
              lineHeight="18px"
              size="tiny"
              weight="regular"
            >
              {formatMoney(
                Number(bill?.totalPrice) +
                  Number(bill?.totalAdjustment) -
                  Number(bill?.deliveryFee)
              ) || '0'}
              đ
            </NameText>
            <NameText
              className="info"
              color="black"
              lineHeight="18px"
              size="tiny"
              weight="bold"
            >
              Tạm tính:
            </NameText>
          </RowInfo>
          <RowInfo justify="flex-end" left={-1}>
            <NameText color="black" lineHeight="18px" size="tiny">
              {formatMoney(bill?.deliveryFee) || '0'}đ
            </NameText>
            <NameText
              className="info"
              color="black"
              lineHeight="18px"
              size="tiny"
              weight="bold"
            >
              Phí giao hàng:
            </NameText>
          </RowInfo>
          <RowInfo justify="flex-end" left={-1}>
            <NameText color="black" lineHeight="18px" size="tiny">
              {formatMoney(bill?.totalAdjustment) || '0'}đ
            </NameText>
            <NameText
              className="info"
              color="black"
              lineHeight="18px"
              size="tiny"
              weight="bold"
            >
              Giảm giá:
            </NameText>
          </RowInfo>
          <RowInfo justify="flex-end" left={-1}>
            <NameText color="black" lineHeight="18px" size="tiny">
              {formatMoney(bill?.totalPrice) || '0'}đ
            </NameText>
            <NameText
              className="info"
              color="black"
              lineHeight="18px"
              size="tiny"
              weight="bold"
            >
              Tổng tiền:
            </NameText>
          </RowInfo>
          <>
            <Hr type="dashed" />
            <div style={{ display: 'inline', width: '100%', minHeight: 38 }}>
              <Text color="black" lineHeight="18px" size="tiny" weight="bold">
                Ghi chú:
              </Text>
              <Text
                color="black"
                lineHeight="18px"
                size="tiny"
                styles={{ 'margin-left': '3px' }}
              >
                {/* {bill?.supplierNote} */}
                {bill?.customerNote}
              </Text>
            </div>
          </>
          <Hr type="dashed" />
          {/* <NameText */}
          {/*  styles={{ 'text-align': 'center' }} */}
          {/*  lineHeight="18px" */}
          {/*  size="small" */}
          {/*  color="black" */}
          {/* > */}
          {/*  Vui lòng quét mã “Thanh toán” để thanh toán đơn hàng, “Đơn hàng” để xem */}
          {/*  thông tin chi tiết đơn hàng */}
          {/* </NameText> */}
          {/* <Space height={4} /> */}
          {/* <Row width="100%" justifyContent="space-around"> */}
          {/*  <Column alignItems="center" width="120px"> */}
          {/*    <CQRCode */}
          {/*      renderAs="svg" */}
          {/*      level="L" */}
          {/*      value={ */}
          {/*        !isWholesaleOrder(bill?.type) */}
          {/*          ? bill.supplier?.domainStatus == DOMAIN_STATUS.ACTIVATE && */}
          {/*            bill.supplier.domain */}
          {/*            ? `${bill.supplier?.domain}/customer/orders/${bill?.id}` */}
          {/*            : `${bill.supplier?.website}/customer/orders/${bill?.id}` */}
          {/*          : `${env.hubtech.hubadminWebApp}/${getEntityUrl( */}
          {/*              bill.supplier?.type */}
          {/*            )}/order/${bill?.id}/detail` || '' */}
          {/*      } */}
          {/*    /> */}
          {/*    <Space height={4} /> */}
          {/*    <NameText */}
          {/*      styles={{ 'text-align': 'center' }} */}
          {/*      weight="extraBold" */}
          {/*      lineHeight="18px" */}
          {/*      size="tiny" */}
          {/*      color="black" */}
          {/*      lineNumber={2} */}
          {/*    > */}
          {/*      Mã thanh toán Momo */}
          {/*    </NameText> */}
          {/*  </Column> */}
          {/* </Row> */}
          <Space height={12} />
          <NameText
            color="black"
            lineHeight="18px"
            size="small"
            styles={{ textAlign: 'center', fontStyle: 'italic' }}
          >
            {`Tổng đài góp ý/khiếu nại: ${formatAppearPhoneNumber(
              bill?.supplier?.phoneNumber
            )}`}
          </NameText>
          <NameText
            color="black"
            lineHeight="18px"
            size="small"
            styles={{ textAlign: 'center', fontStyle: 'italic' }}
          >
            {`Cảm ơn quý khách hàng đã đồng hành cùng ${bill?.supplier?.displayName}`}
          </NameText>
        </>
      )}
    </Wrapper>
  );
};

export default K58BillContent;
